/**
 * Utilidades y funciones helper para el módulo de Liquidación
 * @module liquidacionHelpers
 */

import { getCurrencySymbol } from './dashboardHelpers';

/**
 * Obtiene el color del chip según el estado del pago
 * @param {string} estado - Estado del pago (PENDIENTE, EN_PROCESO, PAGADO, CANCELADO)
 * @returns {string} Color del chip para Material-UI
 */
export const getEstadoColor = (estado) => {
  switch (estado) {
    case 'PENDIENTE':
      return 'warning';
    case 'EN_PROCESO':
      return 'info';
    case 'PAGADO':
      return 'success';
    case 'CANCELADO':
      return 'error';
    default:
      return 'default';
  }
};

/**
 * Formatea un monto a moneda según configuración de empresa
 * @param {number} amount - Monto a formatear
 * @returns {string} Monto formateado con símbolo de moneda dinámico
 */
export const formatCurrency = (amount) => {
  const symbol = getCurrencySymbol();
  const formatted = parseFloat(amount || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${symbol} ${formatted}`;
};

/**
 * Formatea una fecha a formato legible en español
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada (ej: "15 nov 2025, 14:30")
 */
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-PE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calcula el total de comisiones de servicios seleccionados
 * @param {Array} servicios - Array de servicios
 * @param {Array} serviciosSeleccionados - Array de IDs seleccionados
 * @returns {number} Total de comisiones
 */
export const calcularTotalComisiones = (servicios, serviciosSeleccionados) => {
  return servicios
    .filter(s => serviciosSeleccionados.includes(s.id_asignacion))
    .reduce((sum, s) => sum + parseFloat(s.comision_total || 0), 0);
};

/**
 * Calcula el total de egresos seleccionados
 * @param {Array} egresos - Array de egresos
 * @param {Array} egresosSeleccionados - Array de IDs seleccionados
 * @returns {number} Total de egresos
 */
export const calcularTotalEgresos = (egresos, egresosSeleccionados) => {
  return egresos
    .filter(e => egresosSeleccionados.includes(e.id_egreso))
    .reduce((sum, e) => sum + parseFloat(e.monto || 0), 0);
};

/**
 * Valida el formulario de pago
 * @param {Object} pagoForm - Formulario de pago
 * @returns {Array<string>} Array de errores (vacío si no hay errores)
 */
export const validarFormularioPago = (pagoForm) => {
  const errores = [];

  if (!pagoForm.estado) {
    errores.push('Debe seleccionar un estado');
  }

  if (pagoForm.estado === 'PAGADO' && !pagoForm.metodo_pago) {
    errores.push('Método de pago es requerido para marcar como PAGADO');
  }

  if (pagoForm.metodo_pago === 'TRANSFERENCIA' && !pagoForm.numero_operacion) {
    errores.push('Número de operación es requerido para transferencias');
  }

  return errores;
};
